/**
 * Shared constants for tax type configuration.
 *
 * The tax type is a label only; it does not affect any calculation. Rate,
 * calculation method, rounding, inclusive/exclusive pricing and effective dates
 * all live in separate fields on the configuration:tax bundle.
 *
 * These values MUST stay in sync with the allowed_values list in
 * config/d8_sync/field.storage.configuration.field_tax_type.yml. Drupal rejects
 * a save with a value that is not in that list, so adding a type here without
 * the matching config change makes the Tax Settings page fail to persist.
 */

export interface TaxType {
  /** Stored value: matches allowed_values in field.storage.configuration.field_tax_type.yml */
  value: string;
  /** Expanded label for settings dropdowns, where there is room to explain */
  label: string;
  /** Short label for enrollments, agreements and receipts, where space is tight */
  shortLabel: string;
}

export const TAX_TYPES: TaxType[] = [
  { value: 'SALES_TAX', label: 'Sales Tax', shortLabel: 'Sales Tax' },
  { value: 'VAT', label: 'VAT (Value Added Tax)', shortLabel: 'VAT' },
  { value: 'GST', label: 'GST (Goods & Services Tax)', shortLabel: 'GST' },
  { value: 'HST', label: 'HST (Harmonized Sales Tax)', shortLabel: 'HST' },
  { value: 'QST', label: 'QST (Quebec Sales Tax)', shortLabel: 'QST' },
  { value: 'PST', label: 'PST (Provincial Sales Tax)', shortLabel: 'PST' },
  { value: 'SERVICE_TAX', label: 'Service Tax', shortLabel: 'Service Tax' },
  { value: 'EXCISE_TAX', label: 'Excise Tax', shortLabel: 'Excise Tax' },
];

/**
 * Expanded label for a stored tax type value, for settings screens.
 *
 * Falls back to the raw value so a type that exists in Drupal config but not
 * here still renders something recognizable rather than blank.
 */
export function getTaxTypeLabel(value: string): string {
  return TAX_TYPES.find(t => t.value === value)?.label || value;
}

/**
 * Short label for a stored tax type value, for enrollments and agreements.
 *
 * `fallback` is what an unknown or empty type renders as. Callers differ on
 * purpose: the enrollment form shows a generic 'Tax', while the printed
 * agreement has historically defaulted to 'Sales Tax'.
 */
export function getTaxTypeShortLabel(value: string, fallback: string = 'Tax'): string {
  return TAX_TYPES.find(t => t.value === value)?.shortLabel || fallback;
}
